import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, ClockClockwise, ClockCounterClockwise, AirplaneTilt, Camera, Trophy } from "@phosphor-icons/react";

function Stat({ label, value, icon: Icon, color = "#E50914", testid }) {
  return (
    <div className="bg-[#16161C] border border-[#2D2D35] rounded-md p-5 hover:bg-[#1C1C24] transition-colors duration-200 rise" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{label}</div>
          <div className="font-mono text-3xl mt-2 font-semibold">{value ?? "—"}</div>
        </div>
        <div className="p-2 rounded-md border border-[#2D2D35]" style={{ color }}>
          <Icon size={20} weight="bold" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [ranks, setRanks] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setS(r.data)).catch(()=>{});
    api.get("/dashboard/ranking").then((r) => setRanks(r.data.slice(0, 8))).catch(()=>{});
  }, []);

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-2">Control Room</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Today's Operations</h1>
          <p className="text-sm text-zinc-500 mt-2 font-mono">{s?.date}</p>
        </div>
        <a href="/attendance-review" className="text-xs tracking-widest uppercase border border-[#2D2D35] px-4 py-2 rounded-md hover:border-[#E50914] hover:text-[#E50914] transition-colors duration-200">Review Pending →</a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Stat label="Total Staff"    value={s?.total_employees}  icon={Users}              testid="stat-total" />
        <Stat label="Present"        value={s?.present_today}    icon={Camera}             color="#22C55E" testid="stat-present" />
        <Stat label="Absent"         value={s?.absent_today}     icon={ClockCounterClockwise} color="#FF3B30" testid="stat-absent" />
        <Stat label="Late"           value={s?.late_today}       icon={ClockClockwise}     color="#F59E0B" testid="stat-late" />
        <Stat label="Pending Leaves" value={s?.pending_leaves}   icon={AirplaneTilt}       testid="stat-leaves" />
        <Stat label="Photos to Verify" value={s?.pending_attendance_review} icon={Trophy} testid="stat-review" />
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#16161C] border border-[#2D2D35] rounded-md">
          <div className="px-5 py-4 border-b border-[#2D2D35] flex items-center justify-between">
            <div className="text-sm font-medium tracking-wide">Employee Ranking — This Month</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Score 0–100</div>
          </div>
          <table className="w-full" data-testid="ranking-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#2D2D35]">
                <th className="text-left p-3">#</th>
                <th className="text-left p-3">Employee</th>
                <th className="text-left p-3">Shift</th>
                <th className="text-right p-3">Attendance</th>
                <th className="text-right p-3">Punctuality</th>
                <th className="text-right p-3">Discipline</th>
                <th className="text-right p-3">Final</th>
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {ranks.map((r, i) => (
                <tr key={r.id} className="border-b border-[#2D2D35]/60 hover:bg-[#1C1C24]">
                  <td className="p-3 text-zinc-500">{i + 1}</td>
                  <td className="p-3">
                    <div className="text-white">{r.name}</div>
                    <div className="text-[10px] text-zinc-500">{r.employee_id}</div>
                  </td>
                  <td className="p-3 text-zinc-400 text-xs">{r.shift}</td>
                  <td className="p-3 text-right">{r.attendance_score}</td>
                  <td className="p-3 text-right">{r.punctuality_score}</td>
                  <td className="p-3 text-right">{r.discipline_score}</td>
                  <td className="p-3 text-right">
                    <span className={`font-semibold ${r.final_score >= 80 ? "text-[#22C55E]" : r.final_score >= 60 ? "text-[#F59E0B]" : "text-[#FF3B30]"}`}>
                      {r.final_score}
                    </span>
                  </td>
                </tr>
              ))}
              {!ranks.length && (
                <tr><td colSpan="7" className="text-center text-zinc-500 p-6 text-sm">No data yet. Once staff punch attendance, scores appear here.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-[#16161C] border border-[#2D2D35] rounded-md p-5">
          <div className="text-sm font-medium tracking-wide mb-4">Shift Coverage</div>
          {["OFFICE","COLLECTION_A","COLLECTION_B","COLLECTION_C"].map((sh) => (
            <div key={sh} className="py-3 border-b border-[#2D2D35]/60 last:border-0">
              <div className="flex items-center justify-between">
                <div className="text-sm">{sh.replace("_"," ")}</div>
                <div className="font-mono text-xs text-zinc-400">
                  {ranks.filter((r) => r.shift === sh).length} staff
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
